import React, { useState, useEffect } from 'react';
import { XMarkIcon, TrashIcon, PlusIcon } from '@heroicons/react/24/outline';
import { productsAPI, suppliersAPI, catalogueAPI, Product, Supplier, ProductDocument, DocumentType, Category, CostRange } from '../../services/api';
import DocumentUpload, { PendingDocumentUpload } from './DocumentUpload';

interface ProductFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  product?: Product | null;
}

const ProductForm: React.FC<ProductFormProps> = ({ isOpen, onClose, onSuccess, product }) => {
  const [formData, setFormData] = useState({
    sku: '',
    name: '',
    description: '',
    supplierId: '',
    categoryId: '',
    unit: '',
    isActive: true,
    isInventoryLinked: true,
  });
  const [costRanges, setCostRanges] = useState<Partial<CostRange>[]>([]);

  // Inline category creation state
  const [showNewCategory, setShowNewCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryDescription, setNewCategoryDescription] = useState('');
  const [creatingCategory, setCreatingCategory] = useState(false);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [documents, setDocuments] = useState<ProductDocument[]>([]);
  const [pendingDocuments, setPendingDocuments] = useState<PendingDocumentUpload[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      fetchSuppliers();
      fetchCategories();
      if (product) {
        setFormData({
          sku: product.sku,
          name: product.name,
          description: product.description || '',
          supplierId: product.supplierId?.toString() || '',
          categoryId: product.categoryId?.toString() || '',
          unit: product.unit || '',
          isActive: product.isActive,
          isInventoryLinked: product.isInventoryLinked !== false,
        });

        // Load cost ranges if available
        if (product.costRanges && product.costRanges.length > 0) {
          setCostRanges(product.costRanges);
        } else {
          setCostRanges([]);
        }

        fetchDocuments(product.id);
      } else {
        setFormData({
          sku: '',
          name: '',
          description: '',
          supplierId: '',
          categoryId: '',
          unit: '',
          isActive: true,
          isInventoryLinked: true,
        });
        setCostRanges([]);
        setDocuments([]);
        setPendingDocuments([]);
        setShowNewCategory(false);
        setNewCategoryName('');
        setNewCategoryDescription('');
      }
      setError('');
    }
  }, [isOpen, product]);

  const fetchSuppliers = async () => {
    try {
      const data = await suppliersAPI.getAll({});
      setSuppliers(data.data || data);
    } catch (err) {
      console.error('Failed to fetch suppliers:', err);
    }
  };

  const fetchCategories = async () => {
    try {
      const data = await catalogueAPI.getCategories();
      setCategories(data);
    } catch (err) {
      console.error('Failed to fetch categories:', err);
    }
  };

  const fetchDocuments = async (productId: number) => {
    try {
      const docs = await productsAPI.getDocuments(productId);
      setDocuments(docs);
    } catch (err) {
      console.error('Failed to fetch documents:', err);
    }
  };

  const handleUploadDocument = async (
    file: File,
    fileType: DocumentType,
    fileName: string,
    parentDocumentId?: number,
    parentClientId?: string,
  ) => {
    if (product) {
      // If editing existing product, upload immediately
      try {
        await productsAPI.uploadDocument(product.id, file, fileType, fileName, parentDocumentId);
        await fetchDocuments(product.id);
      } catch (err: any) {
        setError(err.response?.data?.message || 'Failed to upload document');
        throw err;
      }
    } else {
      // If creating new product, store documents to upload after creation
      setPendingDocuments([
        ...pendingDocuments,
        {
          clientId: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
          file,
          fileType,
          fileName,
          parentDocumentId,
          parentClientId,
        },
      ]);
    }
  };

  const handleDeleteDocument = async (documentId: number) => {
    try {
      await productsAPI.deleteDocument(documentId);
      if (product) {
        await fetchDocuments(product.id);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete document');
      throw err;
    }
  };

  const handleDeletePendingDocument = (clientId: string) => {
    const blockedIds = new Set<string>([clientId]);
    let changed = true;

    while (changed) {
      changed = false;
      pendingDocuments.forEach((document) => {
        if (document.parentClientId && blockedIds.has(document.parentClientId) && !blockedIds.has(document.clientId)) {
          blockedIds.add(document.clientId);
          changed = true;
        }
      });
    }

    setPendingDocuments(pendingDocuments.filter((document) => !blockedIds.has(document.clientId)));
  };

  const addCostRange = () => {
    setCostRanges([
      ...costRanges,
      { minQuantity: 0, maxQuantity: undefined, unit: formData.unit || undefined, pricePerUnit: '0', currency: 'USD' },
    ]);
  };

  const removeCostRange = (index: number) => {
    setCostRanges(costRanges.filter((_, i) => i !== index));
  };

  const updateCostRange = (index: number, field: string, value: any) => {
    const updated = [...costRanges];
    updated[index] = { ...updated[index], [field]: value };
    setCostRanges(updated);
  };

  const handleCreateCategory = async () => {
    if (!newCategoryName.trim()) return;
    setCreatingCategory(true);
    try {
      const created = await catalogueAPI.createCategory({
        name: newCategoryName.trim(),
        description: newCategoryDescription.trim() || undefined,
      });
      const newCat = created as Category;
      setCategories((prev) => [...prev, newCat]);
      setFormData((prev) => ({ ...prev, categoryId: newCat.id.toString() }));
      setShowNewCategory(false);
      setNewCategoryName('');
      setNewCategoryDescription('');
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to create category');
    } finally {
      setCreatingCategory(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const payload: any = {
        sku: formData.sku,
        name: formData.name,
        isActive: formData.isActive,
        isInventoryLinked: formData.isInventoryLinked,
        costRanges: costRanges.length > 0 
          ? costRanges.map((range) => ({
              minQuantity: range.minQuantity || 0,
              maxQuantity: range.maxQuantity || undefined,
              unit: range.unit || formData.unit || undefined,
              pricePerUnit: parseFloat(range.pricePerUnit?.toString() || '0'),
              currency: range.currency || 'USD',
            }))
          : [],
      };

      if (formData.description) payload.description = formData.description;
      if (formData.supplierId) payload.supplierId = parseInt(formData.supplierId);
      if (formData.categoryId) payload.categoryId = parseInt(formData.categoryId);
      if (formData.unit) payload.unit = formData.unit;

      let createdProductId: number | undefined;

      if (product) {
        await productsAPI.update(product.id, payload);
      } else {
        const createdProduct = await productsAPI.create(payload);
        createdProductId = createdProduct.id;

        // Upload pending documents if any
        if (pendingDocuments.length > 0) {
          const pendingMap = new Map<string, number>();
          const remainingDocuments = [...pendingDocuments];

          while (remainingDocuments.length > 0) {
            let progressed = false;

            for (let index = 0; index < remainingDocuments.length; index += 1) {
              const doc = remainingDocuments[index];
              if (doc.parentClientId && !pendingMap.has(doc.parentClientId)) {
                continue;
              }

              try {
                const createdDocument = await productsAPI.uploadDocument(
                  createdProductId,
                  doc.file,
                  doc.fileType,
                  doc.fileName,
                  doc.parentClientId ? pendingMap.get(doc.parentClientId) : doc.parentDocumentId,
                );
                pendingMap.set(doc.clientId, createdDocument.id);
              } catch (err) {
                console.error('Failed to upload document:', err);
              }

              remainingDocuments.splice(index, 1);
              index -= 1;
              progressed = true;
            }

            if (!progressed) {
              console.error('Some pending child documents could not be linked because their parent was missing.');
              break;
            }
          }
        }
      }

      onSuccess();
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save product');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-neutral-500 bg-opacity-75 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-6 border-b">
          <h3 className="text-lg font-medium text-neutral-900">
            {product ? 'Edit Product' : 'Add Product'}
          </h3>
          <button onClick={onClose} className="text-neutral-400 hover:text-neutral-500">
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="bg-error-50 border border-error-200 text-error-600 px-4 py-3 rounded">
              {error}
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-neutral-700">SKU *</label>
              <input
                type="text"
                required
                value={formData.sku}
                onChange={(e) => setFormData({ ...formData, sku: e.target.value })}
                className="mt-1 block w-full px-3 py-2.5 rounded-lg border border-neutral-300 bg-white shadow-sm focus:border-primary-500 focus:ring-2 focus:ring-primary-500 focus:ring-opacity-50 sm:text-sm transition-colors"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-700">Name *</label>
              <input
                type="text"
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="mt-1 block w-full px-3 py-2.5 rounded-lg border border-neutral-300 bg-white shadow-sm focus:border-primary-500 focus:ring-2 focus:ring-primary-500 focus:ring-opacity-50 sm:text-sm transition-colors"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-neutral-700">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={3}
              className="mt-1 block w-full px-3 py-2.5 rounded-lg border border-neutral-300 bg-white shadow-sm focus:border-primary-500 focus:ring-2 focus:ring-primary-500 focus:ring-opacity-50 sm:text-sm transition-colors"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-neutral-700">Supplier</label>
              <select
                value={formData.supplierId}
                onChange={(e) => setFormData({ ...formData, supplierId: e.target.value })}
                className="mt-1 block w-full px-3 py-2.5 rounded-lg border border-neutral-300 bg-white shadow-sm focus:border-primary-500 focus:ring-2 focus:ring-primary-500 focus:ring-opacity-50 sm:text-sm transition-colors"
              >
                <option value="">Select Supplier</option>
                {suppliers.map((supplier) => (
                  <option key={supplier.id} value={supplier.id}>
                    {supplier.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-span-2">
              <label className="block text-sm font-medium text-neutral-700 mb-1">Category</label>
              {!showNewCategory ? (
                <div className="flex items-center gap-2">
                  <select
                    value={formData.categoryId}
                    onChange={(e) => setFormData({ ...formData, categoryId: e.target.value })}
                    className="flex-1 mt-1 block w-full px-3 py-2.5 rounded-lg border border-neutral-300 bg-white shadow-sm focus:border-primary-500 focus:ring-2 focus:ring-primary-500 focus:ring-opacity-50 sm:text-sm transition-colors"
                  >
                    <option value="">Select Category</option>
                    {categories.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={() => setShowNewCategory(true)}
                    className="mt-1 inline-flex items-center px-3 py-2.5 border border-primary-300 shadow-sm text-sm font-medium rounded-md text-primary-700 bg-white hover:bg-primary-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
                  >
                    <PlusIcon className="h-4 w-4 mr-1" />
                    New Category
                  </button>
                </div>
              ) : (
                <div className="border border-primary-300 rounded-lg bg-primary-50 p-3 space-y-2 mt-1">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium text-primary-700">Create New Category</span>
                    <button
                      type="button"
                      onClick={() => { setShowNewCategory(false); setNewCategoryName(''); setNewCategoryDescription(''); }}
                      className="text-neutral-400 hover:text-neutral-600 transition-colors"
                    >
                      <XMarkIcon className="h-4 w-4" />
                    </button>
                  </div>
                  <input
                    type="text"
                    placeholder="Category name *"
                    value={newCategoryName}
                    onChange={(e) => setNewCategoryName(e.target.value)}
                    className="block w-full border border-neutral-300 rounded-md shadow-sm py-1.5 px-3 text-sm flex-1 bg-white"
                    autoFocus
                  />
                  <input
                    type="text"
                    placeholder="Description (optional)"
                    value={newCategoryDescription}
                    onChange={(e) => setNewCategoryDescription(e.target.value)}
                    className="block w-full border border-neutral-300 rounded-md shadow-sm py-1.5 px-3 text-sm flex-1 bg-white"
                  />
                  <div className="flex gap-2 pt-1">
                    <button
                      type="button"
                      onClick={handleCreateCategory}
                      disabled={!newCategoryName.trim() || creatingCategory}
                      className="inline-flex justify-center py-1.5 px-3 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
                    >
                      {creatingCategory ? 'Creating...' : 'Create'}
                    </button>
                    <button
                      type="button"
                      onClick={() => { setShowNewCategory(false); setNewCategoryName(''); setNewCategoryDescription(''); }}
                      className="inline-flex justify-center py-1.5 px-3 border border-neutral-300 shadow-sm text-sm font-medium rounded-md text-neutral-700 bg-white hover:bg-neutral-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-neutral-700">Unit</label>
            <input
              type="text"
              value={formData.unit}
              onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
              placeholder="e.g., kg, liter, piece"
              className="mt-1 block w-full px-3 py-2.5 rounded-lg border border-neutral-300 bg-white shadow-sm focus:border-primary-500 focus:ring-2 focus:ring-primary-500 focus:ring-opacity-50 sm:text-sm transition-colors"
            />
          </div>

          {/* Cost Ranges */}
          <div>
            <div className="flex justify-between items-center mb-2">
              <div>
                <label className="block text-sm font-medium text-neutral-700">Cost Ranges</label>
                <p className="text-xs text-neutral-500 mt-0.5">Optional — can be added later</p>
              </div>
              <button
                type="button"
                onClick={addCostRange}
                className="inline-flex items-center px-2 py-1 text-xs font-medium text-primary-600 hover:text-primary-700"
              >
                <PlusIcon className="h-4 w-4 mr-1" />
                Add Range
              </button>
            </div>
            
            {costRanges.length === 0 ? (
              <div className="text-center py-4 border-2 border-dashed border-neutral-200 rounded-lg text-neutral-400 text-sm">
                No cost ranges yet — click <strong>Add Range</strong> to add pricing
              </div>
            ) : (
              <div className="space-y-2">
                {costRanges.map((range, index) => (
                  <div key={index} className="flex gap-2 items-start p-3 bg-neutral-50 rounded-lg">
                    <div className="flex-1 grid grid-cols-2 gap-2 lg:grid-cols-4">
                      <div>
                        <label className="block text-xs text-neutral-600 mb-1">Min Qty</label>
                        <input
                          type="number"
                          min="0"
                          value={range.minQuantity || 0}
                          onChange={(e) => updateCostRange(index, 'minQuantity', parseInt(e.target.value))}
                          className="block w-full border border-neutral-300 rounded-md shadow-sm py-1 px-2 text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-neutral-600 mb-1">Max Qty</label>
                        <input
                          type="number"
                          min="0"
                          value={range.maxQuantity || ''}
                          onChange={(e) => updateCostRange(index, 'maxQuantity', e.target.value ? parseInt(e.target.value) : undefined)}
                          placeholder="Unlimited"
                          className="block w-full border border-neutral-300 rounded-md shadow-sm py-1 px-2 text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-neutral-600 mb-1">Unit</label>
                        <input
                          type="text"
                          value={range.unit || ''}
                          onChange={(e) => updateCostRange(index, 'unit', e.target.value)}
                          placeholder={formData.unit || 'unit'}
                          className="block w-full border border-neutral-300 rounded-md shadow-sm py-1 px-2 text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-neutral-600 mb-1">Price/Unit</label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={range.pricePerUnit || 0}
                          onChange={(e) => updateCostRange(index, 'pricePerUnit', e.target.value)}
                          className="block w-full border border-neutral-300 rounded-md shadow-sm py-1 px-2 text-sm"
                        />
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeCostRange(index)}
                      className="text-error-600 hover:text-error-900 mt-5"
                    >
                      <TrashIcon className="h-5 w-5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              checked={formData.isActive}
              onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-neutral-300 rounded"
            />
            <label className="ml-2 block text-sm text-neutral-900">Active</label>
          </div>

          <div className="rounded-lg border border-neutral-200 bg-neutral-50 p-3">
            <label className="flex items-start gap-3 text-sm text-neutral-700">
              <input
                type="checkbox"
                checked={formData.isInventoryLinked}
                onChange={(e) => setFormData({ ...formData, isInventoryLinked: e.target.checked })}
                className="mt-0.5 h-4 w-4 text-primary-600 focus:ring-primary-500 border-neutral-300 rounded"
              />
              <span>
                <span className="block font-medium text-neutral-900">Link to inventory</span>
                <span className="mt-1 block text-neutral-500">
                  Linked products show out-of-stock warnings when quoted.
                </span>
              </span>
            </label>
          </div>

          {/* Documents Section - Available for both create and edit */}
          <div className="border-t pt-4">
            <h4 className="text-sm font-medium text-neutral-900 mb-3">Documents</h4>
            <DocumentUpload
              documents={documents}
              pendingDocuments={pendingDocuments}
              onUpload={handleUploadDocument}
              onDelete={handleDeleteDocument}
              onDeletePending={handleDeletePendingDocument}
              disabled={loading}
              supportsChildDocuments
            />
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-neutral-300 rounded-md text-sm font-medium text-neutral-700 hover:bg-neutral-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
            >
              {loading ? 'Saving...' : product ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProductForm;
